/**
 * Calendar-date helpers shared by API and web. Dates are `YYYY-MM-DD` strings
 * (the user-local day an entry belongs to); instants are RFC 3339 / Date.
 */
const DAY_MS = 86_400_000;
export function toIsoDate(d) {
    return d.toISOString().slice(0, 10);
}
/** Parses `YYYY-MM-DD` into a UTC-midnight Date (calendar arithmetic only). */
export function fromIsoDate(date) {
    return new Date(`${date}T00:00:00Z`);
}
export function isValidIsoDate(date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date))
        return false;
    const d = fromIsoDate(date);
    return !Number.isNaN(d.getTime()) && toIsoDate(d) === date;
}
export function addDays(date, days) {
    return toIsoDate(new Date(fromIsoDate(date).getTime() + days * DAY_MS));
}
export function diffDays(from, to) {
    return Math.round((fromIsoDate(to).getTime() - fromIsoDate(from).getTime()) / DAY_MS);
}
/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date) {
    return fromIsoDate(date).getUTCDay();
}
/** First day of the week containing `date`, honouring the organization's week start (0–6). */
export function startOfWeek(date, weekStartDay) {
    const offset = (dayOfWeek(date) - weekStartDay + 7) % 7;
    return addDays(date, -offset);
}
export function endOfWeek(date, weekStartDay) {
    return addDays(startOfWeek(date, weekStartDay), 6);
}
export function weekDays(date, weekStartDay) {
    const start = startOfWeek(date, weekStartDay);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}
/** `YYYY-MM` of a date. */
export function monthOf(date) {
    return date.slice(0, 7);
}
export function startOfMonth(month) {
    return `${month}-01`;
}
export function endOfMonth(month) {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)); // day 0 of next month = last day of this month
    return toIsoDate(last);
}
export function addMonths(month, delta) {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    return d.toISOString().slice(0, 7);
}
export function isValidMonth(month) {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(month);
}
/** The calendar date of an instant in a given IANA time zone. */
export function localDateOf(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
}
export function todayIn(timeZone, now = new Date()) {
    return localDateOf(now, timeZone);
}
/** Inclusive range for a dashboard period around `date`, plus the immediately preceding range. */
export function periodRanges(period, date, weekStartDay) {
    switch (period) {
        case 'day':
            return {
                current: { from: date, to: date },
                previous: { from: addDays(date, -1), to: addDays(date, -1) },
            };
        case 'week': {
            const from = startOfWeek(date, weekStartDay);
            return {
                current: { from, to: addDays(from, 6) },
                previous: { from: addDays(from, -7), to: addDays(from, -1) },
            };
        }
        case 'month': {
            const month = monthOf(date);
            const prev = addMonths(month, -1);
            return {
                current: { from: startOfMonth(month), to: endOfMonth(month) },
                previous: { from: startOfMonth(prev), to: endOfMonth(prev) },
            };
        }
    }
}
/** Every date in an inclusive range (capped to avoid runaway loops). */
export function eachDay(range, cap = 400) {
    const out = [];
    let d = range.from;
    while (d <= range.to && out.length < cap) {
        out.push(d);
        d = addDays(d, 1);
    }
    return out;
}
/** Offset (minutes east of UTC) of `timeZone` at the given instant. */
export function tzOffsetMinutes(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(instant);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return Math.round((asUtc - instant.getTime()) / 60_000);
}
/** Converts a wall-clock time (`HH:mm`) on `date` in `timeZone` to a UTC instant. */
export function zonedTimeToUtc(date, time, timeZone) {
    const [h = 0, m = 0] = time.split(':').map(Number);
    const naive = new Date(`${date}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00Z`);
    // Two passes converge across DST transitions.
    const offset1 = tzOffsetMinutes(naive, timeZone);
    const guess = new Date(naive.getTime() - offset1 * 60_000);
    const offset2 = tzOffsetMinutes(guess, timeZone);
    return new Date(naive.getTime() - offset2 * 60_000);
}
/** `HH:mm` wall-clock time of an instant in `timeZone`. */
export function localTimeOf(instant, timeZone) {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(instant);
}
//# sourceMappingURL=dates.js.map